import * as React from 'react';
import { RouteChildrenProps } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

import { Section } from '>shared/components/heading/heading';
import { SectionHeading } from '>shared/components/typography/baseTypography2';
import LanguageSelector from '>components/language/languageSelector';
import { Language } from '>shared/lib/language';

import {
  BodyContent,
  Content,
  ContentContainer,
  ContentSection,
  HeadingSection,
  LargeLI,
  PageContainer,
} from '>components/styles/policy.styles';

export const CookiePage: React.FC<RouteChildrenProps> = () => {
  const { t, i18n } = useTranslation('cookie');

  const languageSpecificLink = React.useMemo(() => {
    return i18n.language === Language.French
      ? 'https://optout.aboutads.info/?c=3&lang=fr'
      : 'http://optout.aboutads.info/?c=2&lang=EN';
  }, [i18n.language]);

  return (
    <PageContainer role="main">
      <Trans>
        <ContentContainer>
          <HeadingSection>
            <SectionHeading>{t('cookie:pageTitle')}</SectionHeading>
            <BodyContent>
              {t('cookie:lastUpdated', { date: new Date('December 30 2021') })}
              <LanguageSelector />
            </BodyContent>
          </HeadingSection>
          <Section>
            <ContentSection role="region" aria-label="introduction">
              <Content>{t('cookie:firstParagraph')}</Content>
            </ContentSection>
            <ContentSection role="region" aria-label="technologies">
              <Content>{t('cookie:technologies.title')}</Content>
              <ul>
                <LargeLI>
                  <Content>{t('cookie:technologies.firstBullet')}</Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    <Trans
                      i18nKey="cookie:technologies.secondBullet"
                      components={{
                        flashCookiesLink: (
                          <a href="https://helpx.adobe.com/flash-player/kb/disable-local-shared-objects-flash.html#main_Where_can_I_change_the_settings_for_disabling__or_deleting_local_shared_objects_" />
                        ),
                      }}
                    ></Trans>
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>{t('cookie:technologies.thirdBullet')}</Content>
                </LargeLI>
              </ul>
              <Content>
                <Trans
                  i18nKey="cookie:technologies.firstParagraph"
                  components={{
                    cookieInfoLink: <a href="https://www.privacypolicies.com/blog/cookies/" />,
                  }}
                ></Trans>
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-label="cookieTypesAndPurpose">
              <Content>{t('cookie:purposes.title')}</Content>
              <Content>{t('cookie:purposes.essentialCookies.title')}</Content>
              <Content>{t('cookie:purposes.essentialCookies.type')}</Content>
              <Content>{t('cookie:purposes.essentialCookies.administeredBy')}</Content>
              <Content>{t('cookie:purposes.essentialCookies.purpose')}</Content>
              <Content>{t('cookie:purposes.acceptanceCookies.title')}</Content>
              <Content>{t('cookie:purposes.acceptanceCookies.type')}</Content>
              <Content>{t('cookie:purposes.acceptanceCookies.administeredBy')}</Content>
              <Content>{t('cookie:purposes.acceptanceCookies.purpose')}</Content>
              <Content>{t('cookie:purposes.functionalityCookies.title')}</Content>
              <Content>{t('cookie:purposes.functionalityCookies.type')}</Content>
              <Content>{t('cookie:purposes.functionalityCookies.administeredBy')}</Content>
              <Content>{t('cookie:purposes.functionalityCookies.purpose')}</Content>
              <Content>{t('cookie:purposes.trackingCookies.title')}</Content>
              <Content>{t('cookie:purposes.trackingCookies.type')}</Content>
              <Content>{t('cookie:purposes.trackingCookies.administeredBy')}</Content>
              <Content>{t('cookie:purposes.trackingCookies.purpose')}</Content>
            </ContentSection>
            <ContentSection role="region" aria-label="analytics">
              <Content>{t('cookie:analytics.title')}</Content>
              <Content>{t('cookie:analytics.firstParagraph')}</Content>
              <Content>{t('cookie:analytics.googleAnalytics.title')}</Content>
              <Content>{t('cookie:analytics.googleAnalytics.firstParagraph')}</Content>
              <Content>{t('cookie:analytics.googleAnalytics.secondParagraph')}</Content>
              <Content>
                <Trans
                  i18nKey="cookie:analytics.googleAnalytics.thirdParagraph"
                  components={{
                    googlePrivacyLink: <a href="https://policies.google.com/privacy" />,
                  }}
                ></Trans>
              </Content>
            </ContentSection>
            <ContentSection role="region" aria-label="webAdvertising">
              <Content>{t('cookie:advertising.title')}</Content>
              <Content>{t('cookie:advertising.firstParagraph')}</Content>
              <ul>
                <LargeLI>
                  <Content>
                    <Trans
                      i18nKey="cookie:advertising.firstBullet"
                      components={{
                        naiLink: <a href="http://www.networkadvertising.org/choices/" />,
                      }}
                    ></Trans>
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    <Trans
                      i18nKey="cookie:advertising.secondBullet"
                      components={{
                        edaaLink: <a href="http://www.youronlinechoices.com/" />,
                      }}
                    ></Trans>
                  </Content>
                </LargeLI>
                <LargeLI>
                  <Content>
                    <Trans
                      i18nKey="cookie:advertising.thirdBullet"
                      components={{
                        daaLink: <a href={languageSpecificLink} />,
                      }}
                    ></Trans>
                  </Content>
                </LargeLI>
              </ul>
              <Content>{t('cookie:advertising.secondParagraph')}</Content>
            </ContentSection>
            <ContentSection role="region" aria-label="mobileDevices">
              <Content>{t('cookie:mobileDevices.title')}</Content>
              <Content>{t('cookie:mobileDevices.firstParagraph')}</Content>
              <ul>
                <LargeLI>
                  <Content>{t('cookie:mobileDevices.firstBullet')}</Content>
                </LargeLI>
                <LargeLI>
                  <Content>{t('cookie:mobileDevices.secondBullet')}</Content>
                </LargeLI>
              </ul>
              <Content>{t('cookie:mobileDevices.secondParagraph')}</Content>
            </ContentSection>
            <ContentSection role="region" aria-label="tracking">
              <Content>{t('cookie:tracking.title')}</Content>
              <Content>{t('cookie:tracking.firstParagraph')}</Content>
              <Content>{t('cookie:tracking.secondParagraph')}</Content>
            </ContentSection>
          </Section>
        </ContentContainer>
      </Trans>
    </PageContainer>
  );
};
